import { createTheme, responsiveFontSizes, Theme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { constants } from '../constants';
import { typography } from '../payback_typography';
import { getMuiStyleOverrides } from './themeStyleOverrides';
import {
  Typography,
  TypographyOptions,
} from '@mui/material/styles/createTypography';
import loadCustomFont from '../core/componentLoading/fontLoader';
import { DEFAULT_FIELD_STATES, FieldStates } from './fieldStateConstants';
import { isFieldStatesEnabledForTenant } from '../constants/tenantFeatures';

export interface CustomTheme extends Theme {
  factor?: number;
  custom?: { fieldStates?: FieldStates };
}

export const addCustomThemeProperties = (rawTheme: any): CustomTheme => {
  const factor = rawTheme?.typography?.factor ?? 2;
  const htmlFontSize = Number(rawTheme?.typography?.htmlFontSize ?? 16);

  if (rawTheme?.typography) {
    loadCustomFont(rawTheme);
  }

  const initialTheme = createTheme(rawTheme);

  const baseTheme =
    constants.PATH_FOR_CUSTOM_STATIC_PAGES === 'payback'
      ? deepmerge(initialTheme, { typography, spacing: 16 })
      : initialTheme;

  let customizedTheme = createTheme(baseTheme) as CustomTheme;

  if (htmlFontSize !== undefined) {
    customizedTheme.typography = convertTypographyWithPxToRem(
      customizedTheme
    ) as Typography;
  }

  if (factor !== undefined && typeof factor === 'number') {
    customizedTheme = responsiveFontSizes(customizedTheme, { factor });
    customizedTheme.factor = factor;
  }

  const fieldStatesRaw: FieldStates | undefined =
    isFieldStatesEnabledForTenant()
      ? rawTheme?.fieldStates ?? DEFAULT_FIELD_STATES
      : undefined;
  const fieldStates: FieldStates | undefined = fieldStatesRaw
    ? {
        ...fieldStatesRaw,
        error: { main: customizedTheme.palette.error.main },
        correct: { main: customizedTheme.palette.success.main },
      }
    : undefined;
  if (fieldStates) (customizedTheme as CustomTheme).custom = { fieldStates };

  return deepmerge(customizedTheme, getMuiStyleOverrides(fieldStates));
};

export const convertTypographyWithPxToRem = (
  theme: Theme
): TypographyOptions => {
  const { pxToRem } = theme.typography;
  const typography = theme.typography;
  const convertedTypo: TypographyOptions = { ...typography };

  Object.entries(typography).forEach(([key, value]) => {
    if (['htmlFontSize', 'factor'].includes(key)) return;

    if (typeof value === 'object' && value !== null && 'fontSize' in value) {
      const fontSize = (value as any).fontSize;

      if (typeof fontSize === 'number' && !isNaN(fontSize)) {
        (convertedTypo as any)[key] = {
          ...value,
          fontSize: pxToRem(fontSize),
        };
      }
    }
  });

  return convertedTypo;
};

export const calculateResponsiveFontSize = (
  breakpointMultiplier: number,
  fontSizePx: number,
  theme: CustomTheme
): number => {
  const factor = theme.factor ?? 2.0;
  const htmlFontSize = theme.typography.htmlFontSize ?? 16;

  const maxFontSizeRem = fontSizePx / htmlFontSize;
  const minFontSizeRem = 1 + (maxFontSizeRem - 1) / factor;

  const scalingFactor = (breakpointMultiplier - 1) / (1.8 - 1); // 1.8, xl, default
  const interpolatedSize =
    minFontSizeRem + (maxFontSizeRem - minFontSizeRem) * scalingFactor;

  return interpolatedSize;
};

export const pxOrUndefined = (value?: number) =>
  value && value > 0 ? `${value}px` : undefined;
